<script setup lang="ts">
import { useToast } from '@/use/toast';
import { useI18n } from '@/i18n';
import rest from '@/lib/rest';
import { useUserStore } from '@/stores/user';
import { Alert, Button, Heading, LayoutField, LayoutGap, Prose } from '@dabble/quillui';
import { RestError } from '@dabble/rest-client';
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
// Copies of the gift page's card art (assets.dabblewriter.com/giftcards.png, 1200px and uncached), cut to
// the size they show at so they load with the page.
import birthdayCard from '@/assets/gift-cards/birthday.webp';
import blueCard from '@/assets/gift-cards/blue.webp';
import redCard from '@/assets/gift-cards/red.webp';

const GIFT_PAGE_URL = 'https://www.dabblewriter.com/store/gift-subscriptions';
const COPIED_RESET_MS = 2000;

type CopyTarget = 'link' | 'code';

const { t, te } = useI18n();
const { showToast } = useToast();
const userStore = useUserStore();

const referralCode = ref('');
const isHydrating = ref(true);
const isGenerating = ref(false);
const noBillingAccount = ref(false);
const copied = ref<CopyTarget | null>(null);
const codeInput = ref<HTMLInputElement>();
let copiedTimer: ReturnType<typeof setTimeout> | undefined;

const referralState = computed(() => {
  if (isHydrating.value) return 'loading';
  if (noBillingAccount.value) return 'no-billing';
  return referralCode.value ? 'code' : 'empty';
});

const copiedMessage = computed(() => {
  if (copied.value === 'link') return t('referral_link_copied');
  if (copied.value === 'code') return t('referral_code_copied');
  return '';
});

/** Signup URL with `referralCode` query (see `session-data` sessionParams + `pullSessionFromUrl`). */
function buildReferralSignupLink(): string {
  const url = new URL(`${window.location.origin}/auth/signup`);
  url.searchParams.set('referralCode', referralCode.value);
  return url.href;
}

function restErrorMessage(err: unknown): string {
  if (err instanceof RestError) return err.message;
  if (err instanceof Error) return err.message;
  return String(err);
}

async function fetchReferralCode() {
  isHydrating.value = true;
  noBillingAccount.value = false;
  try {
    const res = (await rest
      .get('/billing/referral-code')
      .header('x-uid', userStore.user?.uid || '')
      .credentials('include')
      .send()) as { code: string | null };
    referralCode.value = res.code?.trim() ?? '';
  } catch (err: unknown) {
    const message = restErrorMessage(err);
    if (message.includes('NO_BILLING_ACCOUNT')) {
      noBillingAccount.value = true;
    } else {
      showToast('danger', te(message) ? t(message) : message);
    }
  } finally {
    isHydrating.value = false;
  }
}

async function generateReferralCode() {
  if (referralState.value !== 'empty') return;
  isGenerating.value = true;
  try {
    const res = (await rest
      .post('/billing/referral-code')
      .header('x-uid', userStore.user?.uid || '')
      .credentials('include')
      .send()) as { code: string };
    referralCode.value = res.code?.trim() ?? '';
    showToast('success', t('referral_toast_generated'));
    await nextTick();
    codeInput.value?.focus();
  } catch (err: unknown) {
    const message = restErrorMessage(err);
    showToast('danger', te(message) ? t(message) : message);
  } finally {
    isGenerating.value = false;
  }
}

async function copy(target: CopyTarget) {
  const text = target === 'link' ? buildReferralSignupLink() : referralCode.value;
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    showToast('danger', t('referral_toast_copy_failed'));
    return;
  }
  copied.value = target;
  clearTimeout(copiedTimer);
  copiedTimer = setTimeout(() => (copied.value = null), COPIED_RESET_MS);
}

onMounted(() => {
  void fetchReferralCode();
});

onBeforeUnmount(() => clearTimeout(copiedTimer));
</script>

<template>
  <LayoutGap direction="column" align="stretch" size="medium" fill class="share-view">
    <LayoutGap direction="column" align="stretch" size="xsmall" fill>
      <Heading :level="1" :semantic-level="1" family="secondary" :margin="false">{{ t('share_title') }}</Heading>
      <Prose variant="secondary">{{ t('share_lede') }}</Prose>
    </LayoutGap>

    <div class="share-view__cards">
      <section class="share-view__card" aria-labelledby="share-referral-title">
        <div class="share-view__visual share-view__visual--referral">
          <div class="share-view__ticket" aria-hidden="true">
            <span class="share-view__ticket-value">
              <span class="share-view__ticket-amount">{{ t('referral_ticket_amount') }}</span>
              <span class="share-view__ticket-off">{{ t('referral_ticket_off') }}</span>
            </span>
            <span class="share-view__ticket-note">{{ t('referral_ticket_note') }}</span>
          </div>
        </div>

        <div class="share-view__body">
          <Heading id="share-referral-title" :level="3" family="secondary" :margin="false" class="share-view__title">
            {{ t('referral_title') }}
          </Heading>
          <Prose variant="secondary">{{ t('referral_description') }}</Prose>

          <div class="share-view__footer">
            <Alert v-if="referralState === 'no-billing'" variant="warning">
              <LayoutGap direction="column" align="left" size="xxsmall">
                <span>{{ t('referral_error_no_billing') }}</span>
                <router-link :to="{ name: 'portal' }" class="share-view__setup-billing">
                  {{ t('referral_setup_billing') }}
                </router-link>
              </LayoutGap>
            </Alert>

            <Button
              v-else-if="referralState === 'empty'"
              variant="primary"
              finish="filled"
              size="large"
              fill-width
              :disabled="isGenerating"
              @click="generateReferralCode"
            >
              {{ t('referral_generate') }}
            </Button>

            <template v-else>
              <LayoutField id="referral-code" :label="t('referral_code_label')">
                <input id="referral-code" ref="codeInput" class="share-view__code" :value="referralCode" readonly />
              </LayoutField>
              <div class="share-view__actions">
                <Button
                  :icon-left="copied === 'link' ? 'check' : 'link'"
                  variant="primary"
                  finish="filled"
                  size="large"
                  :disabled="referralState === 'loading'"
                  @click="copy('link')"
                >
                  {{ copied === 'link' ? t('referral_link_copied') : t('referral_copy_link') }}
                </Button>
                <Button
                  :icon-left="copied === 'code' ? 'check' : 'copy'"
                  variant="primary"
                  finish="outlined"
                  size="large"
                  :disabled="referralState === 'loading'"
                  @click="copy('code')"
                >
                  {{ copied === 'code' ? t('referral_code_copied') : t('referral_copy_code') }}
                </Button>
              </div>
              <span class="share-view__status" role="status">{{ copiedMessage }}</span>
            </template>
          </div>
        </div>
      </section>

      <section class="share-view__card" aria-labelledby="share-gift-title">
        <div class="share-view__visual share-view__visual--gift">
          <div class="share-view__fan">
            <img
              class="share-view__gift-card share-view__gift-card--left"
              :src="blueCard"
              alt=""
              width="132"
              height="88"
            />
            <img
              class="share-view__gift-card share-view__gift-card--right"
              :src="birthdayCard"
              alt=""
              width="126"
              height="84"
            />
            <img
              class="share-view__gift-card share-view__gift-card--front"
              :src="redCard"
              :alt="t('gift_card_alt')"
              width="156"
              height="104"
            />
          </div>
        </div>

        <div class="share-view__body">
          <Heading id="share-gift-title" :level="3" family="secondary" :margin="false" class="share-view__title">
            {{ t('gift_title') }}
          </Heading>
          <Prose variant="secondary">{{ t('gift_description') }}</Prose>

          <div class="share-view__footer">
            <Button
              :href="GIFT_PAGE_URL"
              external
              rel="noopener"
              role="link"
              aria-describedby="share-gift-note"
              icon-right="external"
              variant="primary"
              finish="filled"
              size="large"
              fill-width
              class="share-view__gift-cta"
            >
              {{ t('gift_cta') }}
            </Button>
            <Prose id="share-gift-note" variant="secondary" size="small" align="center">
              {{ t('gift_new_tab_note') }}
            </Prose>
          </div>
        </div>
      </section>
    </div>
  </LayoutGap>
</template>

<style lang="scss" scoped>
.share-view {
  &__cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 260px), 1fr));
    gap: space('small');
  }

  &__card {
    display: flex;
    flex-direction: column;
    border: 1px solid theme('color-border-primary');
    border-radius: borderRadius('medium');
    overflow: hidden;
  }

  &__visual {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 150px;

    &--referral {
      background: theme('color-bg-info');
    }

    &--gift {
      background: theme('color-bg-warning');
    }
  }

  &__ticket {
    display: flex;
    align-items: stretch;
    max-width: calc(100% - #{space('medium')});
    background: theme('color-fg-app');
    border-radius: borderRadius('small');
    box-shadow: 0 8px 20px rgba(38, 58, 89, 0.15);
    color: theme('color-fg-info');
    transform: rotate(-3deg);
  }

  &__ticket-value {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: space('small') space('medium');
    border-right: 2px dashed theme('color-border-info');
  }

  &__ticket-amount {
    font-family: fontFamily('secondary');
    font-size: fontSize('xxlarge');
    font-weight: fontWeight('bold');
    line-height: 1;
  }

  &__ticket-off {
    font-size: fontSize('small');
    font-weight: fontWeight('semibold');
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  &__ticket-note {
    display: flex;
    align-items: center;
    box-sizing: content-box;
    min-width: 0;
    max-width: 92px;
    padding: space('small');
    overflow-wrap: anywhere;
    font-size: fontSize('small');
    line-height: 1.35;
  }

  &__fan {
    position: relative;
    width: 248px;
    height: 128px;
  }

  // The card art has its own rounded corners baked in; a larger radius would clip them.
  &__gift-card {
    position: absolute;
    height: auto;
    border-radius: borderRadius('tiny');

    &--left {
      left: 0;
      top: 6px;
      width: 132px;
      transform: rotate(-9deg);
      box-shadow: 0 8px 18px rgba(27, 25, 24, 0.18);
    }

    &--right {
      right: 0;
      top: 0;
      width: 126px;
      transform: rotate(8deg);
      box-shadow: 0 8px 18px rgba(27, 25, 24, 0.18);
    }

    &--front {
      left: 46px;
      bottom: 0;
      width: 156px;
      transform: rotate(-2deg);
      box-shadow: 0 12px 26px rgba(27, 25, 24, 0.24);
    }
  }

  &__body {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: space('xsmall');
    padding: space('medium');
  }

  &__body &__title {
    font-size: fontSize('medium');
  }

  &__footer {
    margin-top: auto;
    display: flex;
    flex-direction: column;
    gap: space('xsmall');
  }

  &__setup-billing {
    color: theme('color-fg-warning');
    font-weight: fontWeight('semibold');
  }

  &__code {
    box-sizing: border-box;
    width: 100%;
    height: size('xxlarge');
    padding: 0 space('small');
    border: 1.5px dashed theme('color-border-info');
    border-radius: borderRadius('xsmall');
    background: theme('color-bg-info-subtle');
    color: theme('color-text-primary');
    font-family: ui-monospace, 'SF Mono', Menlo, monospace;
    font-size: fontSize('medium');
    letter-spacing: 0.08em;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: space('xxsmall');

    :deep(.button) {
      flex: 1 1 120px;
    }

    @include media-query-below('tablet') {
      flex-direction: column;

      :deep(.button) {
        flex: none;
      }
    }
  }

  &__gift-cta {
    @include focus-outline();
  }

  &__status {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }
}
</style>
